import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Mail, MapPin, Clock } from "lucide-react";
import { Section, Overline, Field } from "@/components/Bits";
import { IMAGES } from "@/data/site";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const Shell = ({ overline, title, lead, children }) => (
  <Section className="grid gap-14 py-16 md:grid-cols-12 md:py-24">
    <div className="md:col-span-5">
      <Overline>{overline}</Overline>
      <h1 className="mt-6 font-display text-4xl font-light leading-[0.95] tracking-tighter sm:text-5xl">{title}</h1>
      <p className="mt-7 max-w-sm text-base leading-relaxed text-zinc-600">{lead}</p>
      <div className="mt-10 hidden aspect-[4/5] overflow-hidden border border-black/10 md:block">
        <img src={IMAGES.portraitCoily} alt="" loading="lazy" className="h-full w-full object-cover object-center" />
      </div>
    </div>
    <div className="md:col-span-6 md:col-start-7">
      <div className="border border-black/10 p-8 md:p-10">{children}</div>
    </div>
  </Section>
);

const submitBtn = "inline-flex items-center justify-center gap-2 rounded-full bg-black px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-zinc-700 disabled:opacity-60";

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { login, homeFor } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email, password);
    setBusy(false);
    if (res.error) return setError(res.error);
    toast.success(t("auth.welcome"));
    navigate(homeFor(res.user));
  };

  return (
    <Shell overline={t("nav.portal")} title={t("auth.login.title")} lead={t("auth.login.lead")}>
      <form onSubmit={submit} data-testid="login-form" className="grid gap-7">
        <Field id="l-email" label={t("form.email")} type="email" value={email} onChange={setEmail} testId="login-email" />
        <Field id="l-password" label={t("form.password")} type="password" value={password} onChange={setPassword} testId="login-password" />
        <Link to="/forgot-password" data-testid="login-forgot-password-link" className="-mt-4 justify-self-end text-xs font-semibold dt-link-underline">
          {t("auth.forgotPassword")}
        </Link>
        {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
        <button type="submit" data-testid="login-submit-btn" disabled={busy} className={submitBtn}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("auth.signin")}
        </button>
        <p className="text-sm text-zinc-500">
          {t("auth.noaccount")}{" "}
          <Link to="/register" data-testid="login-register-link" className="dt-link-underline font-semibold text-black">{t("auth.create")}</Link>
        </p>
      </form>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const { t } = useI18n();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(apiError(err.response?.data?.detail, err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell overline={t("nav.portal")} title={t("auth.forgot.title")} lead={t("auth.forgot.lead")}>
      {sent ? (
        <div className="grid gap-6" data-testid="forgot-password-success">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black text-white"><Mail className="h-5 w-5" /></div>
          <p className="text-sm leading-relaxed text-zinc-600">{t("auth.forgot.sent")}</p>
          <Link to="/login" className={`${submitBtn} text-center`}>{t("auth.backToLogin")}</Link>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="forgot-password-form" className="grid gap-7">
          <Field id="forgot-email" label={t("form.email")} type="email" value={email} onChange={setEmail} testId="forgot-password-email" />
          {error && <p data-testid="forgot-password-error" className="text-sm text-red-600">{error}</p>}
          <button type="submit" data-testid="forgot-password-submit" disabled={busy} className={submitBtn}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("auth.forgot.submit")}
          </button>
          <Link to="/login" className="text-center text-sm font-semibold dt-link-underline">{t("auth.backToLogin")}</Link>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const { t } = useI18n();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError(t("auth.reset.tooShort"));
    if (password !== confirmation) return setError(t("auth.reset.mismatch"));
    setBusy(true);
    try {
      await api.post("/auth/update-password", { password });
      setDone(true);
    } catch (err) {
      setError(apiError(err.response?.data?.detail, err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell overline={t("nav.portal")} title={t("auth.reset.title")} lead={t("auth.reset.lead")}>
      {done ? (
        <div className="grid gap-6" data-testid="reset-password-success">
          <p className="text-sm leading-relaxed text-zinc-600">{t("auth.reset.done")}</p>
          <Link to="/account" className={`${submitBtn} text-center`}>{t("auth.reset.account")}</Link>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="reset-password-form" className="grid gap-7">
          <Field id="reset-password" label={t("auth.reset.password")} type="password" value={password} onChange={setPassword} testId="reset-password" />
          <Field id="reset-confirmation" label={t("auth.reset.confirmation")} type="password" value={confirmation} onChange={setConfirmation} testId="reset-password-confirmation" />
          {error && <p data-testid="reset-password-error" className="text-sm text-red-600">{error}</p>}
          <button type="submit" data-testid="reset-password-submit" disabled={busy} className={submitBtn}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("auth.reset.submit")}
          </button>
        </form>
      )}
    </Shell>
  );
}

export function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "", country: "FR" });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const { register, resendConfirmation } = useAuth();
  const { t, locale } = useI18n();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return setError(t("auth.consentRequired"));
    setBusy(true);
    setError("");
    const res = await register({ ...form, locale, consent_privacy: true });
    setBusy(false);
    if (res.error) return setError(res.error);
    if (res.requires_confirmation) {
      setPendingEmail(form.email.trim().toLowerCase());
      return;
    }
    toast.success(t("auth.opened"));
    navigate("/account");
  };

  const resend = async () => {
    setResendBusy(true);
    setError("");
    const res = await resendConfirmation(pendingEmail);
    setResendBusy(false);
    if (res.error) return setError(res.error);
    toast.success(locale === "fr" ? "E-mail renvoyé." : "Email sent again.");
  };

  const set = (k) => (v) => setForm({ ...form, [k]: v });

  if (pendingEmail) return (
    <Shell overline={t("auth.create")} title={locale === "fr" ? "Confirmez votre e-mail" : "Confirm your email"}
      lead={locale === "fr" ? "Votre compte est presque prêt." : "Your account is almost ready."}>
      <div data-testid="register-confirmation" className="grid gap-6">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black text-white"><Mail className="h-5 w-5" /></div>
        <div>
          <h2 className="font-display text-3xl font-light">
            {locale === "fr" ? "Vérifiez votre boîte de réception" : "Check your inbox"}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-zinc-600">
            {locale === "fr" ? "Nous avons envoyé un lien de confirmation à" : "We sent a confirmation link to"}{" "}
            <strong className="text-black">{pendingEmail}</strong>. {locale === "fr" ? "Cliquez sur ce lien pour activer votre compte." : "Open it to activate your account."}
          </p>
          <p className="mt-3 text-xs leading-relaxed text-zinc-500">
            {locale === "fr" ? "Pensez à vérifier les courriers indésirables. Le lien peut prendre une minute à arriver." : "Check your spam folder too. Delivery can take up to a minute."}
          </p>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button type="button" onClick={resend} disabled={resendBusy} className={submitBtn}>
          {resendBusy && <Loader2 className="h-4 w-4 animate-spin" />}
          {locale === "fr" ? "Renvoyer l'e-mail" : "Send email again"}
        </button>
        <Link to="/login" className="text-center text-sm font-semibold dt-link-underline">
          {locale === "fr" ? "Retour à la connexion" : "Back to sign in"}
        </Link>
      </div>
    </Shell>
  );

  return (
    <Shell overline={t("auth.create")} title={t("auth.register.title")} lead={t("auth.register.lead")}>
      <form onSubmit={submit} data-testid="register-form" className="grid gap-7">
        <Field id="r-name" label={t("form.name")} value={form.name} onChange={set("name")} testId="register-name" />
        <Field id="r-email" label={t("form.email")} type="email" value={form.email} onChange={set("email")} testId="register-email" />
        <Field id="r-password" label={`${t("form.password")} (min. 8)`} type="password" value={form.password} onChange={set("password")} testId="register-password" />
        <Field id="r-country" label={t("form.country")} value={form.country} onChange={set("country")} testId="register-country" hint={t("form.countryHint")} />
        <label className="flex items-start gap-3 text-xs text-zinc-600">
          <input type="checkbox" data-testid="register-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
          <span>{t("auth.consent")}</span>
        </label>
        {error && <p data-testid="register-error" className="text-sm text-red-600">{error}</p>}
        <button type="submit" data-testid="register-submit-btn" disabled={busy} className={submitBtn}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("auth.create")}
        </button>
        <p className="text-sm text-zinc-500">
          {t("auth.hasaccount")}{" "}
          <Link to="/login" data-testid="register-login-link" className="dt-link-underline font-semibold text-black">{t("auth.signin")}</Link>
        </p>
      </form>
    </Shell>
  );
}

export function AuthCallback() {
  const [error, setError] = useState("");
  const { confirmSession } = useAuth();
  const { locale } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    const finish = async () => {
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const query = new URLSearchParams(window.location.search);
      const session = {
        access_token: hash.get("access_token"),
        refresh_token: hash.get("refresh_token"),
        expires_in: hash.get("expires_in"),
      };
      const isRecovery = hash.get("type") === "recovery" || query.get("type") === "recovery";
      const providerError = hash.get("error_description") || query.get("error_description");
      if (providerError || !session.access_token || !session.refresh_token) {
        setError(locale === "fr" ? "Ce lien est invalide ou a expiré. Demandez un nouvel e-mail depuis la page de connexion." : "This link is invalid or expired. Request a new email from the sign-in page.");
        return;
      }
      window.history.replaceState({}, document.title, "/auth/callback");
      const res = await confirmSession(session);
      if (res.error) return setError(res.error);
      if (isRecovery) {
        toast.success(locale === "fr" ? "Lien vérifié. Choisissez votre nouveau mot de passe." : "Link verified. Choose your new password.");
        navigate("/reset-password", { replace: true });
        return;
      }
      toast.success(locale === "fr" ? "Adresse e-mail confirmée." : "Email address confirmed.");
      navigate("/account", { replace: true });
    };
    finish();
  }, [confirmSession, locale, navigate]);

  return (
    <Shell overline="DOCTHAIR" title={locale === "fr" ? "Activation du compte" : "Account activation"}
      lead={locale === "fr" ? "Nous vérifions votre lien sécurisé." : "We are verifying your secure link."}>
      {error ? (
        <div className="grid gap-6" data-testid="callback-error">
          <p className="text-sm leading-relaxed text-red-600">{error}</p>
          <Link to="/login" className={submitBtn}>{locale === "fr" ? "Retour à la connexion" : "Back to sign in"}</Link>
        </div>
      ) : (
        <p className="flex items-center gap-3 text-sm text-zinc-600" data-testid="callback-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> {locale === "fr" ? "Confirmation en cours…" : "Confirming…"}
        </p>
      )}
    </Shell>
  );
}

export function ConfirmEmail() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { locale } = useI18n();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const query = new URLSearchParams(window.location.search);
  const tokenHash = query.get("token_hash");
  const type = query.get("type") || "email";
  const isRecovery = type === "recovery";

  const confirm = async () => {
    if (!tokenHash) {
      setError(locale === "fr" ? "Ce lien est incomplet. Demandez un nouvel e-mail." : "This link is incomplete. Request a new email.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/verify", { token_hash: tokenHash, type });
      window.history.replaceState({}, document.title, "/auth/confirm");
      await refresh();
      if (isRecovery) {
        navigate("/reset-password", { replace: true });
        return;
      }
      toast.success(locale === "fr" ? "Adresse e-mail confirmée." : "Email address confirmed.");
      navigate("/account", { replace: true });
    } catch (err) {
      setError(apiError(err.response?.data?.detail, err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell overline="DOCTHAIR"
      title={isRecovery
        ? (locale === "fr" ? "Réinitialiser mon mot de passe" : "Reset my password")
        : (locale === "fr" ? "Confirmer mon adresse" : "Confirm my email")}
      lead={isRecovery
        ? (locale === "fr" ? "Vérifiez votre lien sécurisé pour choisir un nouveau mot de passe." : "Verify your secure link to choose a new password.")
        : (locale === "fr" ? "Une dernière étape sécurisée avant d’ouvrir votre espace." : "One secure final step before opening your portal.")}>
      <div className="grid gap-6" data-testid="confirm-email">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black text-white"><Mail className="h-5 w-5" /></div>
        <div>
          <h2 className="font-display text-3xl font-light">
            {isRecovery
              ? (locale === "fr" ? "Choisissez un nouveau mot de passe" : "Choose a new password")
              : (locale === "fr" ? "Votre compte est presque prêt" : "Your account is almost ready")}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-zinc-600">
            {isRecovery
              ? (locale === "fr" ? "Cliquez ci-dessous pour vérifier ce lien. Vous pourrez ensuite définir votre nouveau mot de passe." : "Click below to verify this link. You will then be able to set a new password.")
              : (locale === "fr" ? "Cliquez ci-dessous pour confirmer que cette adresse e-mail vous appartient." : "Click below to confirm that this email address belongs to you.")}
          </p>
        </div>
        {error && <p className="text-sm leading-relaxed text-red-600">{error}</p>}
        <button type="button" onClick={confirm} disabled={busy || !tokenHash} className={submitBtn}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {isRecovery
            ? (locale === "fr" ? "Vérifier mon lien de réinitialisation" : "Verify my reset link")
            : (locale === "fr" ? "Confirmer mon adresse" : "Confirm my email")}
        </button>
        {error && (
          <Link to={isRecovery ? "/forgot-password" : "/register"} className="text-center text-sm font-semibold dt-link-underline">
            {isRecovery
              ? (locale === "fr" ? "Demander un nouveau lien" : "Request a new link")
              : (locale === "fr" ? "Créer ou renvoyer mon inscription" : "Create or resend my registration")}
          </Link>
        )}
      </div>
    </Shell>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: "general", message: "" });
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState("");
  const { t } = useI18n();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/contact", form);
      setRef(data.ticket_ref);
      toast.success(data.message);
      setForm({ name: "", email: "", topic: "general", message: "" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section className="py-16 md:py-24">
      <div className="grid gap-14 md:grid-cols-12">
        <div className="md:col-span-5">
          <Overline>{t("nav.contact")}</Overline>
          <h1 className="mt-6 font-display text-4xl font-light leading-[0.95] tracking-tighter sm:text-5xl">{t("contact.title")}</h1>
          <p className="mt-7 max-w-md text-base leading-relaxed text-zinc-600">{t("contact.lead")}</p>
          <div className="mt-10 aspect-[4/3] overflow-hidden border border-black/10">
            <img src={IMAGES.specialist} alt="Conseiller DOCTHAIR" loading="lazy" className="h-full w-full object-cover object-center" />
          </div>
          <div className="mt-8 grid gap-5">
            {[[Mail, "advisors@docthair.com", t("contact.answer24")], [MapPin, "12 rue de Marignan, Paris 8e", t("contact.appointment")], [Clock, t("contact.hours"), t("contact.langs")]].map(([Icon, a, b]) => (
              <div key={a} className="flex items-start gap-4 border-t border-black/10 pt-4">
                <Icon className="mt-0.5 h-4 w-4 text-zinc-500" strokeWidth={1.5} aria-hidden="true" />
                <div><p className="text-sm font-semibold">{a}</p><p className="text-xs text-zinc-500">{b}</p></div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:col-span-6 md:col-start-7">
          <form onSubmit={submit} data-testid="contact-form" className="border border-black/10 p-8 md:p-10">
            <div className="grid gap-7">
              <Field id="c-name" label={t("form.name")} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="contact-name" />
              <Field id="c-email" label={t("form.email")} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="contact-email" />
              <div>
                <span className="overline">{t("contact.topic")}</span>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[["general", t("contact.topic.general")], ["kit", t("contact.topic.kit")], ["results", t("contact.topic.results")], ["pro", t("contact.topic.pro")], ["lab", t("contact.topic.lab")]].map(([v, l]) => (
                    <button key={v} type="button" data-testid={`contact-topic-${v}`} onClick={() => setForm({ ...form, topic: v })}
                      className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${form.topic === v ? "border-black bg-black text-white" : "border-black/15 text-zinc-600 hover:border-black"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="overline" htmlFor="c-msg">{t("form.message")}</label>
                <textarea id="c-msg" data-testid="contact-message" required rows={5} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-2 w-full resize-none border-b border-black/20 bg-transparent pb-2 text-base outline-none focus:border-black" />
              </div>
            </div>
            <button type="submit" data-testid="contact-submit-btn" disabled={busy} className={`mt-8 w-full ${submitBtn}`}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("form.send")}
            </button>
            {ref && <p data-testid="contact-success" className="mt-5 font-mono-dt text-xs text-zinc-600">Ticket {ref} — {t("contact.sent")}</p>}
          </form>
        </div>
      </div>
    </Section>
  );
}
